import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { BarChart3, Users, Heart, FolderOpen, Home, Database, Settings, Clock, ArrowRight, Calendar, TrendingUp, MapPin, Building2 } from 'lucide-react'
import { useNavigate } from 'react-router'
import { usePublications } from '@/hooks/use-publications'
import { useProjects } from '@/hooks/use-projects'
import { useReports } from '@/hooks/use-reports'
import { usePendudukStats } from '@/hooks/use-penduduk-stats'
import useHealth from '@/hooks/use-health'
import { useDesaDetail, useStrukturPemerintahan } from '@/hooks/use-desa-public'
import { useLatestDesil } from '@/hooks/use-latest-desil'
import { OrganizationalChart } from '@/components/OrganizationalChart'
import { LocationCard } from '@/components/LocationCard'
import type { Publication, Project, ProjectReport } from '@/types'

export default function HomePage() {
    const navigate = useNavigate()

    // Fetch latest news data
    const { data: newsData, isLoading: isNewsLoading } = usePublications({
        type: "NEWS",
        limit: 4
    })

    // Fetch recent projects data
    const { data: projectsData, isLoading: isProjectsLoading } = useProjects({
        status: "IN_PROGRESS",
        limit: 3
    })

    // Fetch recent reports data
    const { data: reportsData, isLoading: isReportsLoading } = useReports({
        limit: 3
    })

    // Fetch population statistics
    const { data: pendudukData, isLoading: isPendudukLoading } = usePendudukStats()

    // Fetch health statistics
    const { data: healthData, isLoading: isHealthLoading } = useHealth()

    // Fetch latest desil data
    const { data: desilData, isLoading: isDesilLoading } = useLatestDesil()

    // Fetch desa detail and struktur pemerintahan for new sections
    const { data: desa } = useDesaDetail()
    const { data: strukturPemerintahan } = useStrukturPemerintahan()

    const quickLinks = [
        {
            title: 'Data Penduduk',
            description: 'Lihat statistik dan informasi kependudukan desa',
            icon: Users,
            path: '/statistics'
        },
        {
            title: 'Data Kesehatan',
            description: 'Informasi kesehatan dan layanan medis desa',
            icon: Heart,
            path: '/health-statistics'
        },
        {
            title: 'Berita & Info',
            description: 'Berita terbaru dan pengumuman penting',
            icon: Database,
            path: '/news'
        },
        {
            title: 'Proyek Desa',
            description: 'Pantau progres proyek pembangunan desa',
            icon: FolderOpen,
            path: '/projects'
        }
    ]

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-indigo-50/60">
            {/* Modern Executive Welcome Section */}
            <div className="relative bg-gradient-to-r from-blue-600 via-indigo-700 to-purple-800 overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 bg-black/10">
                    <div className="absolute inset-0 bg-white/5"></div>
                </div>
                
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-white/5 rounded-full"></div>
                <div className="absolute bottom-0 left-0 -mb-32 -ml-32 w-64 h-64 bg-white/5 rounded-full"></div>
                
                <div className="relative container mx-auto px-6 py-20">
                    <div className="max-w-4xl">
                        <div className="flex items-center space-x-3 mb-6">
                            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm border border-white/20">
                                <BarChart3 className="h-8 w-8 text-white" />
                            </div>
                            <div className="flex items-center space-x-2 text-white/80 text-sm">
                                <div className="w-2 h-2 bg-white/60 rounded-full"></div>
                                <span>Portal Desa</span>
                                <div className="w-2 h-2 bg-white/60 rounded-full"></div>
                                <span>Sistem Informasi Desa</span>
                            </div>
                        </div>
                        
                        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
                            {desa?.name || 'Portal Desa'}
                        </h1>
                        <p className="text-white/90 text-xl md:text-2xl font-medium leading-relaxed mb-8 max-w-3xl">
                            {desa?.name ? 
                                `Sistem Informasi ${desa.name} - Platform digital terintegrasi untuk pengelolaan data kependudukan, proyek pembangunan, dan layanan publik desa.` :
                                'Platform terintegrasi untuk pengelolaan data desa, monitoring proyek pembangunan, dan pengambilan keputusan strategis berbasis data.'
                            }
                        </p>
                        
                        <div className="flex flex-wrap gap-4">
                            <Button 
                                size="lg" 
                                className="bg-white text-blue-700 hover:bg-blue-50 font-semibold px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1" 
                                onClick={() => navigate('/statistics')}
                            >
                                <Users className="h-5 w-5 mr-2" />
                                Lihat Penduduk
                            </Button>
                            <Button 
                                size="lg" 
                                variant="outline" 
                                className="border-2 border-white text-white bg-white/10 hover:bg-white hover:text-blue-700 font-semibold px-8 py-3 rounded-xl backdrop-blur-sm transition-all duration-300 hover:-translate-y-1" 
                                onClick={() => navigate('/projects')}
                            >
                                <FolderOpen className="h-5 w-5 mr-2" />
                                Lihat Proyek
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-6 py-12 space-y-12">

                {/* Enhanced Quick Links Section */}
                <div className="py-16 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden rounded-3xl mb-12">
                    {/* Background decoration */}
                    <div className="absolute top-10 left-10 w-64 h-64 bg-blue-100 rounded-full opacity-50 blur-3xl"></div>
                    <div className="absolute bottom-20 right-20 w-48 h-48 bg-purple-100 rounded-full opacity-40 blur-2xl"></div>
                    
                    <div className="relative z-10 px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-gray-900 bg-clip-text text-transparent mb-6" style={{ lineHeight: '1.4' }}>
                                Layanan Unggulan
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
                                Akses berbagai layanan dan informasi yang Anda butuhkan dengan mudah dan cepat
                            </p>
                            
                            {/* Decorative line */}
                            <div className="mt-8 flex justify-center">
                                <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {quickLinks.map((link, index) => (
                                <Card key={link.title} className="group bg-white/80 backdrop-blur-sm border border-gray-200 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 rounded-3xl overflow-hidden relative">
                                    {/* Background gradient overlay - moved to Card level for full coverage */}
                                    <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                                        index === 0 ? 'bg-gradient-to-br from-blue-500/10 to-blue-600/5' :
                                        index === 1 ? 'bg-gradient-to-br from-green-500/10 to-green-600/5' :
                                        index === 2 ? 'bg-gradient-to-br from-orange-500/10 to-orange-600/5' :
                                        'bg-gradient-to-br from-purple-500/10 to-purple-600/5'
                                    } rounded-3xl`}></div>
                                    <CardContent className="p-8 text-center relative z-10">
                                        <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 shadow-lg ${
                                            index === 0 ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                                            index === 1 ? 'bg-gradient-to-br from-green-500 to-green-600' :
                                            index === 2 ? 'bg-gradient-to-br from-orange-500 to-orange-600' :
                                            'bg-gradient-to-br from-purple-500 to-purple-600'
                                        }`}>
                                            <link.icon className="h-8 w-8 text-white" />
                                        </div>
                                        
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                                            {link.title}
                                        </h3>
                                        
                                        <p className="text-gray-600 mb-6 leading-relaxed">
                                            {link.description}
                                        </p>
                                        
                                        <Button 
                                            onClick={() => navigate(link.path)}
                                            className={`w-full font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform group-hover:scale-105 shadow-lg ${
                                                index === 0 ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white' :
                                                index === 1 ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white' :
                                                index === 2 ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white' :
                                                'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white'
                                            }`}
                                        >
                                            Akses Sekarang
                                        </Button>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Organizational Structure and Location Sections */}
                <div className="space-y-8 mb-12">
                    {/* Organizational Structure Section */}
                    {strukturPemerintahan && (
                        <Card className="bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl overflow-hidden pt-0 pb-6">
                            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 pb-4 pt-6 px-6 rounded-t-2xl">
                                <CardTitle className="flex items-center gap-3 text-xl font-bold text-gray-800">
                                    <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                                        <Building2 className="h-6 w-6 text-white" />
                                    </div>
                                    Struktur Organisasi
                                </CardTitle>
                                <CardDescription className="text-gray-600 font-medium">
                                    Struktur kepemimpinan dan organisasi pemerintahan desa
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="p-6">
                                <OrganizationalChart showHeader={false} />
                                <Button
                                    variant="outline"
                                    className="w-full mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 text-blue-700 hover:bg-gradient-to-r hover:from-blue-100 hover:to-indigo-100 font-semibold rounded-xl py-2 h-auto"
                                    onClick={() => navigate('/desa')}
                                >
                                    Lihat Detail Struktur <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </CardContent>
                        </Card>
                    )}

                    {/* Village Location Section */}
                    {desa && desa.latitude && desa.longitude && (
                        <Card className="bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl overflow-hidden pt-0 pb-6">
                            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 pb-4 pt-6 px-6 rounded-t-2xl">
                                <CardTitle className="flex items-center gap-3 text-xl font-bold text-gray-800">
                                    <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg">
                                        <MapPin className="h-6 w-6 text-white" />
                                    </div>
                                    Lokasi Desa
                                </CardTitle>
                                <CardDescription className="text-gray-600 font-medium">
                                    Peta dan navigasi menuju kantor desa
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="p-6">
                                <LocationCard desa={desa} showHeader={false} />
                                <Button
                                    variant="outline"
                                    className="w-full mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-700 hover:bg-gradient-to-r hover:from-green-100 hover:to-emerald-100 font-semibold rounded-xl py-2 h-auto"
                                    onClick={() => navigate('/desa/lokasi')}
                                >
                                    Lihat Peta Detail <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Statistics Overview Section */}
                <div className="space-y-8 mb-12">
                    {/* Population Statistics */}
                    <Card className="bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl overflow-hidden pt-0 pb-6">
                        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 pb-4 pt-6 px-6 rounded-t-2xl">
                            <CardTitle className="flex items-center gap-3 text-xl font-bold text-gray-800">
                                <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                                    <Users className="h-6 w-6 text-white" />
                                </div>
                                Statistik Penduduk
                            </CardTitle>
                            <CardDescription className="text-gray-600 font-medium">
                                Data kependudukan desa terkini
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="p-6">
                            {isPendudukLoading ? (
                                <div className="space-y-4">
                                    <div className="animate-pulse">
                                        <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Stats Cards */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                                            <div className="text-2xl font-bold text-blue-600 mb-1">
                                                {pendudukData?.data?.totalPenduduk || 0}
                                            </div>
                                            <div className="text-sm text-gray-600">Total Penduduk</div>
                                        </div>
                                        <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
                                            <div className="text-2xl font-bold text-green-600 mb-1">
                                                {pendudukData?.data?.totalKepalaKeluarga || 0}
                                            </div>
                                            <div className="text-sm text-gray-600">Kepala Keluarga</div>
                                        </div>
                                        <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
                                            <div className="text-2xl font-bold text-purple-600 mb-1">
                                                {pendudukData?.data?.jumlahLakiLaki || 0}
                                            </div>
                                            <div className="text-sm text-gray-600">Laki-laki</div>
                                        </div>
                                        <div className="text-center p-4 bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl">
                                            <div className="text-2xl font-bold text-pink-600 mb-1">
                                                {pendudukData?.data?.jumlahPerempuan || 0}
                                            </div>
                                            <div className="text-sm text-gray-600">Perempuan</div>
                                        </div>
                                    </div>
                                    
                                    {/* Gender Distribution Chart */}
                                    <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6">
                                        <h4 className="font-semibold text-gray-800 mb-4">Distribusi Jenis Kelamin</h4>
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-600">Laki-laki</span>
                                                <span className="text-sm font-medium text-purple-600">
                                                    {pendudukData?.data?.persentaseLakiLaki?.toFixed(1) || 0}%
                                                </span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-3">
                                                <div 
                                                    className="bg-gradient-to-r from-purple-500 to-purple-600 h-3 rounded-full transition-all duration-500"
                                                    style={{ width: `${pendudukData?.data?.persentaseLakiLaki || 0}%` }}
                                                ></div>
                                            </div>
                                            
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-600">Perempuan</span>
                                                <span className="text-sm font-medium text-pink-600">
                                                    {pendudukData?.data?.persentasePerempuan?.toFixed(1) || 0}%
                                                </span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-3">
                                                <div 
                                                    className="bg-gradient-to-r from-pink-500 to-pink-600 h-3 rounded-full transition-all duration-500"
                                                    style={{ width: `${pendudukData?.data?.persentasePerempuan || 0}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <Button
                                variant="outline"
                                className="w-full mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 text-blue-700 hover:bg-gradient-to-r hover:from-blue-100 hover:to-indigo-100 font-semibold rounded-xl py-2 h-auto"
                                onClick={() => navigate('/statistics')}
                            >
                                Lihat Detail Statistik <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Health Statistics */}
                    <Card className="bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl overflow-hidden pt-0 pb-6">
                        <CardHeader className="bg-gradient-to-r from-red-50 to-rose-50 pb-4 pt-6 px-6 rounded-t-2xl">
                            <CardTitle className="flex items-center gap-3 text-xl font-bold text-gray-800">
                                <div className="p-2 bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg">
                                    <Heart className="h-6 w-6 text-white" />
                                </div>
                                Statistik Kesehatan
                            </CardTitle>
                            <CardDescription className="text-gray-600 font-medium">
                                Data kesehatan masyarakat desa
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="p-6">
                            {isHealthLoading ? (
                                <div className="space-y-4">
                                    <div className="animate-pulse">
                                        <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Stats Cards */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                                            <div className="text-2xl font-bold text-blue-600 mb-1">
                                                {healthData?.data?.totalTerdaftar || 0}
                                            </div>
                                            <div className="text-sm text-gray-600">Total Terdaftar</div>
                                        </div>
                                        <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
                                            <div className="text-2xl font-bold text-green-600 mb-1">
                                                {healthData?.data?.totalGiziNormal || 0}
                                            </div>
                                            <div className="text-sm text-gray-600">Gizi Normal</div>
                                        </div>
                                        <div className="text-center p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-xl">
                                            <div className="text-2xl font-bold text-red-600 mb-1">
                                                {healthData?.data?.totalStunting || 0}
                                            </div>
                                            <div className="text-sm text-gray-600">Stunting</div>
                                        </div>
                                        <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl">
                                            <div className="text-2xl font-bold text-orange-600 mb-1">
                                                {healthData?.data?.totalGiziBuruk || 0}
                                            </div>
                                            <div className="text-sm text-gray-600">Gizi Buruk</div>
                                        </div>
                                    </div>
                                    
                                    {/* Nutrition Status Chart */}
                                    <div className="bg-gradient-to-br from-gray-50 to-green-50 rounded-xl p-6">
                                        <h4 className="font-semibold text-gray-800 mb-4">Status Gizi</h4>
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-600">Gizi Normal</span>
                                                <span className="text-sm font-medium text-green-600">
                                                    {healthData?.data?.persentaseGiziNormal?.toFixed(1) || 0}%
                                                </span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-3">
                                                <div 
                                                    className="h-3 rounded-full transition-all duration-500"
                                                    style={{ 
                                                        width: `${healthData?.data?.persentaseGiziNormal || 0}%`,
                                                        backgroundColor: '#10b981'
                                                    }}
                                                ></div>
                                            </div>
                                            
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-600">Stunting</span>
                                                <span className="text-sm font-medium text-red-600">
                                                    {healthData?.data?.persentaseStunting?.toFixed(1) || 0}%
                                                </span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-3">
                                                <div 
                                                    className="h-3 rounded-full transition-all duration-500"
                                                    style={{ 
                                                        width: `${healthData?.data?.persentaseStunting || 0}%`,
                                                        backgroundColor: '#ef4444'
                                                    }}
                                                ></div>
                                            </div>
                                            
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-600">Gizi Buruk</span>
                                                <span className="text-sm font-medium text-orange-600">
                                                    {healthData?.data?.persentaseGiziBuruk?.toFixed(1) || 0}%
                                                </span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-3">
                                                <div 
                                                    className="h-3 rounded-full transition-all duration-500"
                                                    style={{ 
                                                        width: `${healthData?.data?.persentaseGiziBuruk || 0}%`,
                                                        backgroundColor: '#f97316'
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
            <Button
                variant="outline"
                className="w-full mt-4 bg-gradient-to-r from-red-50 to-rose-50 border-red-200 text-red-700 hover:bg-gradient-to-r hover:from-red-100 hover:to-rose-100 font-semibold rounded-xl py-2 h-auto"
                onClick={() => navigate('/health-statistics')}
            >
                Lihat Detail Kesehatan <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
        </CardContent>
    </Card>

    {/* Desil Kesejahteraan Statistics */}
    <Card className="bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl overflow-hidden pt-0 pb-6">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-violet-50 pb-4 pt-6 px-6 rounded-t-2xl">
            <CardTitle className="flex items-center gap-3 text-xl font-bold text-gray-800">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg">
                    <TrendingUp className="h-6 w-6 text-white" />
                </div>
                Data Desil Kesejahteraan Terbaru
            </CardTitle>
            <CardDescription className="text-gray-600 font-medium">
                Distribusi desil kesejahteraan masyarakat desa terkini
            </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
            {isDesilLoading ? (
                <div className="space-y-4">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </div>
                </div>
            ) : desilData?.data ? (
                <div className="space-y-6">
                    {/* Main Info Card */}
                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-100">
                        <div className="text-center space-y-4">
                            <div>
                                <h4 className="font-bold text-2xl text-gray-800">Tahun {desilData.data.tahun}</h4>
                                <p className="text-lg text-gray-600">Tahap {desilData.data.tahap}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                                <div className="bg-white/80 rounded-lg p-4 border border-purple-200">
                                    <div className="text-2xl font-bold text-purple-600">{desilData.data.total.toLocaleString('id-ID')}</div>
                                    <div className="text-sm text-gray-600">Total</div>
                                </div>
                                <div className="bg-white/80 rounded-lg p-4 border border-green-200">
                                    <div className="text-2xl font-bold text-green-600">{desilData.data.jumlahKpm.toLocaleString('id-ID')}</div>
                                    <div className="text-sm text-gray-600">Jumlah KPM</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Desil Distribution Bar Chart */}
                    <div className="bg-gradient-to-br from-gray-50 to-purple-50 rounded-xl p-6 border border-purple-100">
                        <h4 className="font-semibold text-gray-800 mb-6">Distribusi Desil Kesejahteraan</h4>
                        <div className="space-y-4">
                            {[
                                { label: 'Desil 1', value: desilData.data.desil1, color: '#ef4444', bgColor: '#fef2f2' },
                                { label: 'Desil 2', value: desilData.data.desil2, color: '#f97316', bgColor: '#fff7ed' },
                                { label: 'Desil 3', value: desilData.data.desil3, color: '#eab308', bgColor: '#fefce8' },
                                { label: 'Desil 4', value: desilData.data.desil4, color: '#3b82f6', bgColor: '#eff6ff' },
                                { label: 'Desil 5', value: desilData.data.desil5, color: '#10b981', bgColor: '#f0fdf4' },
                                { label: 'Desil 6-10', value: desilData.data.desil6_10, color: '#059669', bgColor: '#ecfdf5' },
                                ...(desilData.data.belumDiperingkat > 0 ? [{ label: 'Belum Diperingkat', value: desilData.data.belumDiperingkat, color: '#f59e0b', bgColor: '#fff7ed' }] : [])
                            ].map((item, index) => {
                                const maxValue = Math.max(
                                    desilData.data?.desil1 || 0,
                                    desilData.data?.desil2 || 0,
                                    desilData.data?.desil3 || 0,
                                    desilData.data?.desil4 || 0,
                                    desilData.data?.desil5 || 0,
                                    desilData.data?.desil6_10 || 0,
                                    desilData.data?.belumDiperingkat || 0
                                );
                                const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
                                
                                return (
                                    <div key={index} className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-700">{item.label}</span>
                                            <span className="text-sm font-bold" style={{ color: item.color }}>
                                                {item.value.toLocaleString('id-ID')}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-5 overflow-hidden">
                                            <div 
                                                className="h-5 rounded-full transition-all duration-700 ease-out shadow-sm"
                                                style={{ 
                                                    width: `${percentage}%`,
                                                    backgroundColor: item.color,
                                                    backgroundImage: `linear-gradient(45deg, ${item.color}dd, ${item.color})`
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="text-center py-8">
                    <div className="text-gray-500">Belum ada data desil kesejahteraan tersedia</div>
                </div>
            )}
            <Button
                variant="outline"
                className="w-full mt-6 bg-gradient-to-r from-purple-50 to-violet-50 border-purple-200 text-purple-700 hover:bg-gradient-to-r hover:from-purple-100 hover:to-violet-100 font-semibold rounded-xl py-2 h-auto"
                onClick={() => navigate('/desil-statistics')}
            >
                Lihat Detail Statistik Desil <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
        </CardContent>
    </Card>
</div>                {/* Latest News Section */}
                <div className="mb-12">
                    <Card className="bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl overflow-hidden pt-0 pb-6">
                        <CardHeader className="bg-gradient-to-r from-orange-50 to-orange-100 pb-4 pt-6 px-6 rounded-t-2xl">
                            <CardTitle className="flex items-center gap-3 text-xl font-bold text-gray-800">
                                <div className="p-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg">
                                    <Database className="h-6 w-6 text-white" />
                                </div>
                                Berita Terbaru
                            </CardTitle>
                            <CardDescription className="text-gray-600 font-medium">
                                Informasi dan berita terkini dari desa
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="p-6">
                            {isNewsLoading ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {[...Array(4)].map((_, i) => (
                                        <div key={i} className="animate-pulse bg-white rounded-xl overflow-hidden shadow-sm">
                                            <div className="h-32 bg-gray-200"></div>
                                            <div className="p-4">
                                                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                                <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                                                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {newsData?.data?.slice(0, 4).map((article: Publication, index) => (
                                        <div 
                                            key={article.id} 
                                            className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer border border-gray-100"
                                            onClick={() => navigate(`/news/${article.slug}`)}
                                        >
                                            {/* News Image */}
                                            <div className="h-32 relative overflow-hidden">
                                                {article.featuredImage ? (
                                                    <img 
                                                        src={article.featuredImage} 
                                                        alt={article.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-gradient-to-br from-orange-500 to-orange-600"></div>
                                                )}
                                                <div className="absolute inset-0 bg-black/20"></div>
                                                <div className="absolute bottom-2 left-2 right-2">
                                                    <span className="px-2 py-1 rounded-full font-medium bg-white/90 text-orange-800 text-xs">
                                                        {article.type}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            {/* News Content */}
                                            <div className="p-4">
                                                <h4 className="font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors line-clamp-2">{article.title}</h4>
                                                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{article.summary}</p>
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-gray-500 flex items-center gap-1">
                                                        <Clock className="h-3 w-3" />
                                                        {article.publishedAt ? new Date(article.publishedAt).toLocaleDateString() : 'Draft'}
                                                    </span>
                                                    <span className="text-orange-600 font-medium">Baca selengkapnya</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="mt-6 flex justify-end">
                                <Button
                                    variant="outline"
                                    className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200 text-orange-700 hover:bg-gradient-to-r hover:from-orange-100 hover:to-orange-200 font-semibold rounded-xl px-6 py-2 h-auto"
                                    onClick={() => navigate('/news')}
                                >
                                    Lihat Semua Berita <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Enhanced Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Enhanced Recent Projects */}
                    <Card className="bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl overflow-hidden pt-0 pb-6">
                        <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 pb-4 pt-6 px-6 rounded-t-2xl">
                            <CardTitle className="flex items-center gap-3 text-xl font-bold text-gray-800">
                                <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg">
                                    <FolderOpen className="h-6 w-6 text-white" />
                                </div>
                                Recent Projects
                            </CardTitle>
                            <CardDescription className="text-gray-600 font-medium">
                                Current ongoing projects and initiatives
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="p-6">
                            {isProjectsLoading ? (
                                <div className="space-y-4">
                                    {[...Array(3)].map((_, i) => (
                                        <div key={i} className="animate-pulse">
                                            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                            <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                                            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {projectsData?.data?.slice(0, 3).map((project: Project) => (
                                        <div 
                                            key={project.id} 
                                            className="group p-4 bg-gradient-to-r from-gray-50 to-green-50/50 rounded-xl border border-gray-100 hover:shadow-md transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                                            onClick={() => navigate(`/projects/${project.id}`)}
                                        >
                                            <div className="flex gap-3">
                                                {project.image && (
                                                    <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                                        <img 
                                                            src={project.image} 
                                                            alt={project.title}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">{project.title}</h4>
                                                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{project.description}</p>
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-4 text-xs text-gray-500">
                                                            <span className="flex items-center gap-1">
                                                                <Calendar className="h-3 w-3" />
                                                                {new Date(project.startDate).toLocaleDateString()}
                                                            </span>
                                                            {project.progress !== undefined && (
                                                                <span className="font-medium text-blue-600">{project.progress}% complete</span>
                                                            )}
                                                        </div>
                                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${project.status === 'COMPLETED'
                                                                ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800'
                                                                : project.status === 'IN_PROGRESS'
                                                                    ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800'
                                                                    : 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800'
                                                            }`}>
                                                            {project.status.replace('_', ' ')}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <Button
                                        variant="outline"
                                        className="w-full mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-700 hover:bg-gradient-to-r hover:from-green-100 hover:to-emerald-100 font-semibold rounded-xl py-2 h-auto"
                                        onClick={() => navigate('/projects')}
                                    >
                                        View All Projects <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Enhanced Recent Project Reports */}
                    <Card className="bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl overflow-hidden pt-0 pb-6">
                        <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 pb-4 pt-6 px-6 rounded-t-2xl">
                            <CardTitle className="flex items-center gap-3 text-xl font-bold text-gray-800">
                                <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg">
                                    <BarChart3 className="h-6 w-6 text-white" />
                                </div>
                                Recent Project Reports
                            </CardTitle>
                            <CardDescription className="text-gray-600 font-medium">
                                Latest project progress and milestone reports
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="p-6">
                            {isReportsLoading ? (
                                <div className="space-y-4">
                                    {[...Array(3)].map((_, i) => (
                                        <div key={i} className="animate-pulse">
                                            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                            <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                                            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {reportsData?.data?.slice(0, 3).map((report: ProjectReport) => (
                                        <div 
                                            key={report.id} 
                                            className="group p-4 bg-gradient-to-r from-gray-50 to-purple-50/50 rounded-xl border border-gray-100 hover:shadow-md transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                                            onClick={() => navigate(`/reports/${report.slug}`)}
                                        >
                                            <div className="flex gap-3">
                                                {report.featuredImage && (
                                                    <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                                        <img 
                                                            src={report.featuredImage} 
                                                            alt={report.title}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-semibold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">{report.title}</h4>
                                                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{report.summary}</p>
                                                    <div className="flex items-center justify-between text-xs">
                                                        <span className="text-gray-500 flex items-center gap-1">
                                                            <Clock className="h-3 w-3" />
                                                            {new Date(report.reportDate).toLocaleDateString()}
                                                        </span>
                                                        <span className={`px-3 py-1 rounded-full font-medium ${report.type === 'PROGRESS'
                                                                ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800'
                                                                : report.type === 'MILESTONE'
                                                                    ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800'
                                                                    : 'bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800'
                                                            }`}>
                                                            {report.type}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <Button
                                        variant="outline"
                                        className="w-full mt-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200 text-purple-700 hover:bg-gradient-to-r hover:from-purple-100 hover:to-indigo-100 font-semibold rounded-xl py-2 h-auto"
                                        onClick={() => navigate('/reports')}
                                    >
                                        View All Reports <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    )
}
